import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { isNil } from "lodash";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { exhaustive } from "shared/switch";
import PageLayout from "../components/common/page-layout";
import {
  SchemaRunTestStatus,
  useSchemaRunTestListLazyQuery,
} from "../generated/graphql";
import { RequireAuth } from "../hooks/use-auth";
import { schemaRunPath } from "../route-paths";
import { filenameFromKey } from "../utils/file";

const styles = {
  results: {
    color: "black",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  rowActions: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  expectedResultsButton: { mr: "10px" },
} satisfies Record<string, SxProps<Theme>>;

const getStatusColor = (
  status: SchemaRunTestStatus | null | undefined,
): string => {
  switch (status) {
    case null:
    case undefined: {
      return "black";
    }
    case SchemaRunTestStatus.Passed: {
      return "green";
    }
    case SchemaRunTestStatus.Failed: {
      return "red";
    }
    case SchemaRunTestStatus.Error: {
      return "red";
    }
    case SchemaRunTestStatus.InProgress: {
      return "yellow";
    }
    default: {
      return exhaustive(status);
    }
  }
};

/** One schema test list's runs and their pass/fail status. */
export const SchemaTestListPage = () => {
  const { uuid } = useParams();
  const [schemaRunTestListQuery, { data, loading }] =
    useSchemaRunTestListLazyQuery({
      variables: {
        schemaRunTestListInput: {
          schemaRunTestListUuid: uuid ?? "",
        },
      },
      pollInterval: 5000,
    });

  useEffect(() => {
    schemaRunTestListQuery();
  }, []);

  const content = (() => {
    if (loading) {
      return <Typography>Loading...</Typography>;
    }
    if (isNil(data)) {
      return <Typography>No data</Typography>;
    }
    return (
      <Box sx={styles.results}>
        <Typography
          variant="h2"
          sx={{
            color: getStatusColor(data.schemaRunTestList.schemaRunTestStatus),
          }}
        >
          Overall status: {data.schemaRunTestList.schemaRunTestStatus}
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Test</TableCell>
                <TableCell>Status</TableCell>
                <TableCell> </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.schemaRunTestList.schemaRuns.map((schemaRun) => {
                return (
                  <TableRow key={schemaRun.uuid}>
                    <TableCell>
                      {schemaRun.schema.name} {filenameFromKey(schemaRun.key)}
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color: getStatusColor(schemaRun.schemaRunTestStatus),
                        }}
                      >
                        {schemaRun.schemaRunTestStatus}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={styles.rowActions}>
                        {!isNil(schemaRun.schemaRunTestSnapshot?.uuid) && (
                          <Button
                            sx={styles.expectedResultsButton}
                            variant="contained"
                            onClick={() => {
                              window.open(
                                schemaRunPath(
                                  schemaRun.schemaRunTestSnapshot?.uuid ?? "",
                                ),
                                "_blank",
                              );
                            }}
                          >
                            View expected results
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          onClick={() => {
                            window.open(
                              schemaRunPath(schemaRun.uuid),
                              "_blank",
                            );
                          }}
                        >
                          View test results
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  })();

  return (
    <RequireAuth>
      <PageLayout
        maxWidth={false}
        subtitle="Legacy schema-matching pipeline"
        title="Schema test list"
      >
        {content}
      </PageLayout>
    </RequireAuth>
  );
};
